import { SEO } from '@/features/SEO';
import { ContentContainer } from '@/features/shared';
import { MuiContainer } from '@/features/shared/material-ui';
import { ColorNames, InsetTop, SpacingSizes } from '@/utils/constants';
import { getDocumentBasedImage, parseHTMLString } from '@/utils/helpers';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import TechParnerCTA from '../shared/CTA/CTA';
import { MainContent } from '../shared/MainContent';
import { IntroContent } from './IntroContent';
import { TechPartnerDetailProps } from './TechPartnerDetail.interface';
import useStyles from './TechPartnerDetail.styles';

const TechPartnerDetail = ({
  backgroundBannerImage,
  backgroundBannerVideo,
  headline,
  summary,
  includeBreadcrumb,
  includeJumplinks = false,
  labels,
  logo,
  mobileBackgroundBanner,
  partnerWebsiteUrl,
  pageMetadata,
  shortDescription,
  socialChannels,
  memberProfile,
  contentButtons,
  cardImage = {},
}: Br.PropsWithContentButtons<TechPartnerDetailProps>) => {
  const {
    cta,
    detailContent = [],
    includeJumplinks: mpIncludeJumplinks = false,
  } = memberProfile || {};

  const classes = useStyles({});
  const pathSplit = useLocation().pathname.split('/');
  const partnerName = useMemo(() => pathSplit[pathSplit.length - 1], [pathSplit]);

  const { primaryCTA = {}, secondaryCTA = {} } = cta?.ctaContent.content || {};

  const addJumplinks = useMemo(
    () => includeJumplinks || mpIncludeJumplinks,
    [includeJumplinks, mpIncludeJumplinks],
  );

  const introProps = {
    headline,
    includeBreadcrumb,
    addJumplinks,
    labels,
    logo,
    partnerName,
    partnerWebsiteUrl,
    shortDescription,
    socialChannels,
    summary,
  };

  const checkCtaExists = (priCTA: LinkType, secCTA?: LinkType) => {
    let hasCta =
      'displayText' in priCTA &&
      priCTA.displayText !== '' &&
      'href' in priCTA &&
      priCTA.href !== '';
    if (secCTA) {
      hasCta =
        hasCta &&
        'displayText' in secCTA &&
        secCTA.displayText !== '' &&
        'href' in secCTA &&
        secCTA.href !== '';
    }
    return hasCta;
  };

  const hasCta = useMemo(
    () => checkCtaExists(primaryCTA, secondaryCTA),
    [primaryCTA, secondaryCTA],
  );

  const containerProps = useMemo(
    () => ({
      backgroundImage: backgroundBannerImage,
      mobileBackgroundImage: mobileBackgroundBanner,
      backgroundVideo: backgroundBannerVideo,
      bottomBackgroundColor: ColorNames.eggshell,
      bottomSpacing: SpacingSizes.small,
      offsetTop: true,
      topBackgroundColor: backgroundBannerVideo ? 'transparent' : ColorNames.eggshell,
      topSpacing: SpacingSizes.small,
      insetTop: InsetTop.hero,
    }),
    [backgroundBannerImage, backgroundBannerVideo, mobileBackgroundBanner],
  );

  const metadata = {
    ...pageMetadata,
    metaTitle: pageMetadata?.metaTitle || headline,
    description: pageMetadata?.description || parseHTMLString(shortDescription),
    socialMediaImage: getDocumentBasedImage({ pageMetadata, cardImage }),
  };

  const rootClasses = clsx(classes.root);
  return (
    <article id="tech-partner-details" className={rootClasses}>
      <ContentContainer className={classes.container} {...containerProps}>
        <MuiContainer className="large">
          {contentButtons?.content}
          <IntroContent {...introProps} />
        </MuiContainer>
      </ContentContainer>
      <section>
        <MainContent detailContent={detailContent} />
      </section>
      {hasCta && <TechParnerCTA cta={cta} />}
      {metadata && <SEO metadata={metadata} isDocumentBasedPage />}
    </article>
  );
};

export default TechPartnerDetail;
